import type { GpuAiResult } from "~/types/gpu";

/**
 * 調整 GPU 結果，使訓練時間變為整數小時
 *
 * 計算邏輯：
 * - 總訓練時數 = training_hours (固定)
 * - 單個伺服器訓練時數 = training_hours * stock_quantity
 * - 調整後的 stock_quantity = ceil(單個伺服器訓練時數 / 整數小時)
 * - 調整後的 training_hours = 整數小時
 *
 * 注意：如果訓練時間超過 24 小時，不進行調整
 *
 * @param result - 原始 GPU 結果
 * @returns 調整後的 GPU 結果
 */
export const adjustGpuResultToWholeHours = (result: GpuAiResult): GpuAiResult => {
  const { training_hours, stock_quantity, total_cost } = result;

  // 如果訓練時間超過 24 小時，不需要調整
  if (training_hours > 24) {
    return result;
  }

  // 如果訓練時間已經是整數小時，不需要調整
  if (Number.isInteger(training_hours)) {
    return result;
  }

  // 計算總 GPU 時數 = training_hours * stock_quantity
  // 這是完成訓練所需的總 GPU-小時數
  const totalGpuHours = training_hours * stock_quantity;

  // 將訓練時間向上取整到最接近的整數小時
  const adjustedTrainingHours = Math.ceil(training_hours);

  // 重新計算需要的伺服器數量
  // 為了保持總 GPU 時數不變，我們需要調整 stock_quantity
  const adjustedStockQuantity = Math.ceil(totalGpuHours / adjustedTrainingHours);

  // 重新計算總成本
  // 假設成本與 (training_hours * stock_quantity) 成正比
  const costPerGpuHour = total_cost / totalGpuHours;
  const adjustedTotalCost = Math.round(costPerGpuHour * adjustedTrainingHours * adjustedStockQuantity * 100) / 100;

  return {
    ...result,
    training_hours: adjustedTrainingHours,
    stock_quantity: adjustedStockQuantity,
    total_cost: adjustedTotalCost
  };
};

/**
 * 批量調整 GPU 結果陣列
 *
 * @param results - 原始 GPU 結果陣列
 * @returns 調整後的 GPU 結果陣列
 */
export const adjustGpuResults = (results: GpuAiResult[]): GpuAiResult[] => {
  return results.map(result => adjustGpuResultToWholeHours(result));
};
